import type { ReactElement } from 'react';
import { useCallback, useMemo, useState } from 'react';

import type { ErrorLike } from '@apollo/client';
import {
  useApolloClient,
  useFragment as useApolloFragment,
  useMutation,
} from '@apollo/client/react';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import IconButton from '@mui/material/IconButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';
import { useFileDialog } from '@realadvisor/hooks';
import { EllipsisVerticalIcon, EyeIcon, Trash2Icon } from 'lucide-react';

import {
  type FileFragment,
  type StepAppendAddPropertyFileMutation,
  type StepAppendFilesFragment,
} from '@graphql/__generated__/graphql';
import { FullScreenDropZone } from '@modules/_shared/components/DropZone';
import { FileDeleteDialog } from '@modules/_shared/components/FileDeleteDialog';
import { FileIcon } from '@modules/_shared/components/FileIcon';
import { FileViewerDialog } from '@modules/_shared/components/FileViewerDialog';
import {
  type FormDefinitionType,
  RaForm,
} from '@modules/_shared/components/form/RaForm';
import RASnackbar from '@modules/_shared/components/RASnackbar';
import {
  type ReportComponentProps,
  useReportContext,
} from '@modules/_shared/components/report-editor/ReportEditorContext';
import { SortableTable } from '@modules/_shared/components/sortable-list/SortableTable';
import type {
  ReorderEvent,
  SortableTableColDef,
} from '@modules/_shared/components/sortable-list/types';
import { UploadImageCard } from '@modules/_shared/components/UploadCard';
import {
  type EntityFileUploadData,
  useEntityFileUpload,
} from '@modules/_shared/hooks/useEntityFileUpload';
import {
  STEP_ADD_CMA_REPORT_FILE,
  STEP_APPEND_ADD_PROPERTY_FILE,
  STEP_APPEND_FILES_FRAGMENT,
  STEP_DELETE_CMA_REPORT_FILE,
  STEP_UPDATE_CMA_REPORT_FILE,
  STEP_UPDATE_CMA_REPORT_FILE_VISIBILITY,
} from '@modules/reports/cma-reports/cmaReportsQueries';
import { FooterActions } from '@modules/reports/shared/FooterActionsComponentWithoutForm';
import { useLocale } from '@shared/hooks/locale';

export type CmaReportFileItem = {
  id: string;
  is_visible: boolean;
  file: FileFragment;
  cma_report_id: string;
  order_nr: number;
};

type CmaReportFile = NonNullable<
  StepAppendFilesFragment['cma_report_files'][number]
>;

const StepAppendFiles = (props: ReportComponentProps): ReactElement => {
  const { reportId: cmaReportId } = props;
  const { t } = useLocale();
  const { cache } = useApolloClient();
  const { setCurrentStepByIndex, currentStepIndex } = useReportContext();
  const [error, setError] = useState<Error | null>(null);
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [menuItem, setMenuItem] = useState<CmaReportFileItem | null>(null);

  const { data, complete } = useApolloFragment({
    fragment: STEP_APPEND_FILES_FRAGMENT,
    fragmentName: 'StepAppendFiles',
    from: {
      __typename: 'cma_reports',
      id: cmaReportId,
    },
  });

  const cmaReportFiles = useMemo(
    () => data?.cma_report_files ?? [],
    [data?.cma_report_files],
  );
  const propertyId = data?.property?.id;

  const [addCmaReportFile, { loading: addingCmaFile }] = useMutation(
    STEP_ADD_CMA_REPORT_FILE,
  );

  const [updateCmaReportFile, { loading: updatingCmaFile }] = useMutation(
    STEP_UPDATE_CMA_REPORT_FILE,
  );

  const [updateVisibility, { loading: updatingVisibility }] = useMutation(
    STEP_UPDATE_CMA_REPORT_FILE_VISIBILITY,
  );

  const [deleteCmaReportFile] = useMutation(STEP_DELETE_CMA_REPORT_FILE);

  const [viewerFile, setViewerFile] = useState<
    CmaReportFileItem['file'] | null
  >(null);
  const [deleteFile, setDeleteFile] = useState<CmaReportFileItem | null>(null);

  const sortableFiles = useMemo(
    (): CmaReportFileItem[] =>
      cmaReportFiles
        .filter(
          (item): item is CmaReportFile =>
            item != null &&
            item.file?.id != null &&
            item.is_visible != null &&
            item.cma_report_id != null &&
            item.order_nr != null,
        )
        .map(item => ({
          id: item.file.id,
          is_visible: item.is_visible,
          file: item.file,
          cma_report_id: item.cma_report_id,
          order_nr: item.order_nr,
        })),
    [cmaReportFiles],
  );

  const handleReorder = useCallback(
    ({ movedItem, toIndex }: ReorderEvent<CmaReportFileItem>) => {
      const targetOrderNr = cmaReportFiles[toIndex]?.order_nr;

      updateCmaReportFile({
        variables: {
          file_id: movedItem.file.id,
          cma_report_id: cmaReportId,
          input: { order_nr: targetOrderNr },
        },
      });
    },
    [cmaReportFiles, cmaReportId, updateCmaReportFile],
  );

  const [createPropertyFile] = useMutation(STEP_APPEND_ADD_PROPERTY_FILE, {
    update: async (_, { data }) => {
      if (data?.insert_property_files_one) {
        await addCmaReportFile({
          variables: {
            object: {
              cma_report_id: cmaReportId,
              file_id: data.insert_property_files_one.file_id,
              is_visible: true,
            },
            cma_report_id: cmaReportId,
          },
        });
      }
    },
  });

  const createFile = useCallback(
    (
      file: EntityFileUploadData,
      onCompleted: (data: StepAppendAddPropertyFileMutation) => void,
      onError: (error: ErrorLike) => void,
    ) => {
      if (propertyId == null) {
        return;
      }

      createPropertyFile({
        variables: {
          object: {
            file: {
              data: file,
            },
            property_id: propertyId,
          },
        },
        onCompleted,
        onError,
      });
    },
    [createPropertyFile, propertyId],
  );

  const { upload, pendingFiles, FileErrors } =
    useEntityFileUpload<StepAppendAddPropertyFileMutation>({
      createFile,
    });

  const handleDrop = (files: File[]) => {
    const pdfFiles = files.filter(file => file.type === 'application/pdf');
    const ignoredCount = files.length - pdfFiles.length;

    if (ignoredCount > 0) {
      setError(
        new Error(
          t('{{count}} files were ignored because they are not PDFs', {
            count: ignoredCount,
          }),
        ),
      );
    }

    pdfFiles.forEach(file => upload(file));
  };

  const openFileDialog = useFileDialog({
    accept: 'application/pdf',
    onChange: handleDrop,
  });

  const handleVisibilityChange = useCallback(
    (fileId: string, isVisible: boolean) => {
      updateVisibility({
        variables: {
          file_id: fileId,
          cma_report_id: cmaReportId,
          is_visible: isVisible,
        },
      });
    },
    [cmaReportId, updateVisibility],
  );

  const handleMenuOpen = (
    event: React.MouseEvent<HTMLElement>,
    item: CmaReportFileItem,
  ) => {
    event.stopPropagation();
    setMenuAnchorEl(event.currentTarget);
    setMenuItem(item);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setMenuItem(null);
  };

  const updating = addingCmaFile || updatingCmaFile || updatingVisibility;

  const columns: SortableTableColDef<CmaReportFileItem>[] = useMemo(
    () => [
      {
        field: 'file',
        headerName: t('File'),
        flex: 1,
        renderCell: ({ row }) => (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              minWidth: 0,
            }}
          >
            <FileIcon
              mimetype={row.file.mimetype ?? ''}
              filename={row.file.name}
              size="medium"
            />
            <Typography
              noWrap
              sx={{
                flex: 1,
                minWidth: 0,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {row.file.name}
            </Typography>
          </Box>
        ),
      },
      {
        field: 'is_visible',
        headerName: t('Attach to CMA'),
        width: 150,
        align: 'center',
        renderCell: ({ row }) => (
          <Checkbox
            size="small"
            checked={row.is_visible}
            onChange={evt =>
              handleVisibilityChange(row.file.id, evt.target.checked)
            }
          />
        ),
      },
      {
        field: 'actions',
        width: 40,
        align: 'center',
        renderCell: ({ row }) => (
          <IconButton size="small" onClick={e => handleMenuOpen(e, row)}>
            <EllipsisVerticalIcon size={18} />
          </IconButton>
        ),
      },
    ],
    [t, handleVisibilityChange],
  );

  const renderUploadingItem = useCallback(
    (item: { name: string; type?: string }) => (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          minWidth: 0,
        }}
      >
        <FileIcon
          mimetype={item.type ?? ''}
          filename={item.name}
          size="medium"
        />
        <Typography
          noWrap
          sx={{
            flex: 1,
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            color: 'text.secondary',
          }}
        >
          {item.name}
        </Typography>
      </Box>
    ),
    [],
  );

  const uploadingItems = useMemo(
    () =>
      pendingFiles.map((file, index) => ({
        id: `uploading-${file.name}-${index}`,
        name: file.name,
        type: file.type,
      })),
    [pendingFiles],
  );

  const stepAppendFilesFormDefinition: FormDefinitionType = () => [
    {
      type: 'custom',
      name: 'append_files',
      gridProps: { md: 12 },
      element: (
        <>
          {viewerFile && (
            <FileViewerDialog
              open
              url={viewerFile?.url ?? ''}
              name={viewerFile?.name ?? ''}
              onClose={() => setViewerFile(null)}
            />
          )}
          <FileDeleteDialog
            open={!!deleteFile}
            onDelete={() => {
              cache.evict({
                id: cache.identify({
                  __typename: 'cma_report_files',
                  cma_report_id: deleteFile?.cma_report_id,
                  file_id: deleteFile?.file.id,
                }),
              });
              cache.evict({
                id: cache.identify({
                  __typename: 'property_files',
                  property_id: propertyId,
                  file_id: deleteFile?.file.id,
                }),
              });

              deleteCmaReportFile({
                variables: {
                  file_id: deleteFile?.file.id ?? '',
                  cma_report_id: cmaReportId,
                },
              });
            }}
            fileId={deleteFile?.file.id ?? ''}
            onClose={() => setDeleteFile(null)}
          />
          <FullScreenDropZone onDrop={handleDrop} disabled={false} accept="*" />
          <Box
            sx={{
              mt: -1,
              mb: 1,
              mx: -2,
            }}
          >
            <Box
              sx={{
                px: 1,
              }}
            >
              <UploadImageCard noMinHeight onClick={openFileDialog} />
            </Box>
          </Box>
          <Box
            sx={{
              mx: -2,
              mt: 1,
            }}
          >
            <SortableTable
              items={sortableFiles}
              columns={columns}
              onReorder={handleReorder}
              enableDragAndDrop
              uploadingItems={uploadingItems}
              renderUploadingItem={renderUploadingItem}
              showHeader
              loading={!complete}
            />
          </Box>
          <Menu
            anchorEl={menuAnchorEl}
            open={Boolean(menuAnchorEl)}
            onClose={handleMenuClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
          >
            {menuItem?.file.mimetype === 'application/pdf' && (
              <MenuItem
                onClick={() => {
                  if (menuItem) {
                    setViewerFile(menuItem.file);
                  }
                  handleMenuClose();
                }}
              >
                <ListItemIcon>
                  <EyeIcon size={18} />
                </ListItemIcon>
                <ListItemText>{t('Preview')}</ListItemText>
              </MenuItem>
            )}
            <MenuItem
              onClick={() => {
                if (menuItem) {
                  setDeleteFile(menuItem);
                }
                handleMenuClose();
              }}
            >
              <ListItemIcon>
                <Trash2Icon size={18} />
              </ListItemIcon>
              <ListItemText>{t('Delete')}</ListItemText>
            </MenuItem>
          </Menu>
          <FileErrors />
        </>
      ),
    },
  ];

  const handleSubmit = async () => {
    setCurrentStepByIndex(currentStepIndex + 1);
  };

  return (
    <>
      <RaForm
        formDefinition={stepAppendFilesFormDefinition}
        onSubmit={handleSubmit}
        actionButtonsComponent={
          <FooterActions
            {...props}
            saveLabel={t('Share report')}
            updating={updating}
          />
        }
        contentScrollable
      />

      {error && (
        <RASnackbar message={error.message} onClose={() => setError(null)} />
      )}
    </>
  );
};

export default StepAppendFiles;
